import { ThunkAction } from "redux-thunk";
import {
  IAccountReconciliationDetails,
  IAccountReconciliationDetailsList,
  IAccountReconciliationDocument,
  IAIExcelFileExtractHeadersPayload,
  IBillExtraction,
  IComparisonDetails,
  IExtractedPdfData,
  ITempBillPayment,
  ITempInvoicePayment,
  IUploadPdfDocumentProcess,
} from "./aiReconciliation.types";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { makeApiCall } from "../../helpers/postRequest";
import { api } from "../../api/api";
import { action } from "typesafe-actions";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IAgencyBillReconciliation } from "../reconciliation/reconciliation.types";

function transform(obj: any) {
  const result: any = {};

  Object.entries(obj).forEach(([key, value]) => {
    const match = key.match(/^(.*)_(import|db)$/); // only keys ending with _import or _db
    if (match) {
      const [, baseKey, type] = match;
      if (!result[baseKey]) {
        result[baseKey] = { import: null, db: null };
      }
      result[baseKey][type] = value;
    }
  });

  return result;
}

export const getExcelFileHeaderAsync =
  (
    excel_payload: IAIExcelFileExtractHeadersPayload,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(openLoaderWithMessage("File is being processed..."));
      const res = await makeApiCall(
        {
          url: "/general/get-excel-header",
          method: "POST",
        },
        undefined,
        {
          url: `${process.env["REACT_APP_SERVER_URL"]}/general/get-signed-url?key=${excel_payload.url}`,
          start_row: Number(excel_payload.heading_row_start || "0"),
          end_row: Number(excel_payload.heading_row_end || "0"),
          type: excel_payload.type,
          project_name: excel_payload.project_name,
          project_instructions: excel_payload.project_instructions,
          // insurer: excel_payload.insurer,
          // broker_code: excel_payload.broker_code,
          // bank: excel_payload.bank,
          // date: excel_payload.date,
        }
      );

      dispatch(
        showMessage({
          type: "success",
          message: "File Processed successfully",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };

export const upsertAccountReconciliationDocument =
  (
    data: IAccountReconciliationDocument,
    onCallback: (
      isSuccess: boolean,
      data?: IAccountReconciliationDocument
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const { create_ts, insert_ts, ...rest } = data;
      const payload = {
        ...rest,
        file_type: rest.file_type === "EXCEL" ? "XLSX" : rest.file_type,
      };
      const res = await api.post(
        "/accounting/edit-account-reconciliation-document",
        payload
      );

      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertAIBillPaymentDocument =
  (
    data: ITempBillPayment,
    onCallback: (
      isSuccess: boolean,
      data?: ITempBillPayment
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const { create_ts, insert_ts, ...rest } = data;
      const payload = {
        ...rest,
        file_type: rest.file_type === "EXCEL" ? "XLSX" : rest.file_type,
      };
      const res = await api.post(
        "/import-utility/edit-temp-credit-card",
        payload
      );

      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
export const upsertAInvoicePaymentDocument =
  (
    data: ITempInvoicePayment,
    onCallback: (
      isSuccess: boolean,
      data?: ITempInvoicePayment
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const { create_ts, insert_ts, ...rest } = data;
      const payload = {
        ...rest,
        file_type: rest.file_type === "EXCEL" ? "XLSX" : rest.file_type,
      };
      const res = await api.post(
        "/import-utility/edit-temp-bank-statement-of-invoice-and-bill",
        payload
      );

      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertAIBillExtraction =
  (
    billPaymentCode: string,
    onCallback: (
      isSuccess: boolean,
      data?: IBillExtraction[]
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/import-utility/import-credit-card-extractions",
        {
          bill_payment_code: billPaymentCode,
        }
      );


      onCallback(true, res.data.data.bill_details_result);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const upsertAInvoiceExtraction =
  (
    invoicePaymentCode: string,
    type: string,
    onCallback: (
      isSuccess: boolean,
      data?: any[]
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/import-utility/import-bank-statement-of-invoice-and-bill-extractions",
        {
          invoice_payment_code: invoicePaymentCode,
          type: type === "bill" ? "payment": "invoice",
        }
      );


      onCallback(true, res.data.data.payment_details_result);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const updateAIBillExtractionStatus =
  (
    accnTransactionId: number[],
    onCallback: (
      isSuccess: boolean,
      updatedId: number[]
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/import-utility/update-temporary-credit-card-status",
        {
          accn_transaction_id: accnTransactionId,
        }
      );

      const updatedId = res.data.data.updated_ids;


      onCallback(true, updatedId);
    } catch (err: any) {
      onCallback(false, []);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    } 
  };
export const updateAInvoiceExtractionStatus =
  (
    paymentId: number[],
    type: string,
    onCallback: (
      isSuccess: boolean,
      updatedId: number[]
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/import-utility/update-temporary-bank-statement-of-invoice-and-bill-status",
        {
          temp_invoice_payment_id: paymentId,
          type: type === "bill" ? "payment": "invoice",
        },
      );


      onCallback(true, res.data.data.updated_ids);
    } catch (err: any) {
      onCallback(false, []);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchReconciliationDetailsDocumentAsync =
  (
    documentCode: string,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const res = await api.get(
        `/accounting/get-account-reconciliation-document?reconciliation_document_code=${documentCode}&pageNo=1&itemPerPage=1`
      );

      const finalDocument: IAccountReconciliationDocument[] = res.data.data;
      if (finalDocument.length > 0) {
        onCallback(true, finalDocument[0]);
      } else {
        onCallback(false);
      }
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const extractDataFromPdfAsync =
  (
    reconciliation_document_code: string,
    onCallback: (
      isSuccess: boolean,
      data?: {
        process_records_code: string;
        reconciliation_document_code: string;
      }
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/general/extract-data-from-pdf", {
        reconciliation_document_code,
      });

      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetcheExtractedDataFromPdfAsync =
  (
    reconciliation_document_code: string,
    onCallback: (isSuccess: boolean, data?: IExtractedPdfData) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res =
        await api.get(`/general/get-extracted-pdf-data?reconciliation_document_code=${reconciliation_document_code}
`);
      const finalData: IExtractedPdfData[] = res.data.data;
      if (finalData.length > 0) {
        onCallback(true, res.data.data[0]);
      } else {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: ERROR_MESSAGES.NO_RECORD_FOUND,
            displayAs: "snackbar",
          })
        );
      }
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const getUploadedPdfProccess =
  (
    process_records_code: string,
    onCallback: (
      isSuccess: boolean,
      process?: IUploadPdfDocumentProcess
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/general/get-process?process_records_code=${process_records_code}&pageNo=1&itemPerPage=1`
      );
      const data: IUploadPdfDocumentProcess[] = res.data.data;
      onCallback(true, data[0]);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const extractProductsFromExcelFileAsync =
  (
    payload: {
      reconciliation_template_code: string;
      reconciliation_template_name: string;
      reconciliation_document_code: string;

      file_type: string;
      template_mapping_data: any;
      status: "ACTIVE";
      start_row?: number;
      end_row?: number;
    },
    commissionData: {
      commission_paid: string | null;
      payment_paid_date: string | null;
      producer_paid: string | null;
      producer_paid_date: string | null;
      company_paid: string | null;
      company_paid_date: string | null;
      date_format: string[] | null;
      default_transaction_date: string | null;
      assigned_to_code: string | null;
      assigned_to_id: string | null;
      assigned_to_name: string | null;
      insurer: string | null;
      broker_code: string | null;
      bank: string | null;
      date: string | null;
      // reconcile_for?: string;
      bank_reconciled?: string;
      bank_reconciled_date?: string;
    },
    onCallback: (
      isSuccess: boolean,
      data?: {
        reconciliation_template_code: string;
        reconciliation_document_code: string;
      }
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        reconciliation_template_code,
        reconciliation_document_code,
        start_row,
        end_row,
      } = payload;

      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/accounting/edit-account-reconciliation-template",
        reconciliation_template_code
          ? {
              reconciliation_document_code,
              reconciliation_template_code,
              start_row: start_row,
              end_row: end_row,
              data_for_transaction_items: {
                commission_paid: commissionData.commission_paid,
                payment_paid_date: commissionData.payment_paid_date,
                producer_paid: commissionData.producer_paid,
                producer_paid_date: commissionData.producer_paid_date,
                company_paid: commissionData.company_paid,
                company_paid_date: commissionData.company_paid_date,
                date_format: commissionData.date_format,
                default_transaction_date:
                  commissionData.default_transaction_date,
                assigned_to_code: commissionData.assigned_to_code,
                assigned_to_id: commissionData.assigned_to_id,
                assigned_to_name: commissionData.assigned_to_name,
                //  reconcile_for: commissionData.reconcile_for,
                bank_reconciled: commissionData.bank_reconciled,
                bank_reconciled_date: commissionData.bank_reconciled_date,
              },
            }
          : {
              ...payload,
              data_for_transaction_items: {
                commission_paid: commissionData.commission_paid,
                payment_paid_date: commissionData.payment_paid_date,
                producer_paid: commissionData.producer_paid,
                producer_paid_date: commissionData.producer_paid_date,
                company_paid: commissionData.company_paid,
                company_paid_date: commissionData.company_paid_date,
                date_format: commissionData.date_format,
                default_transaction_date:
                  commissionData.default_transaction_date,
                assigned_to_code: commissionData.assigned_to_code,
                assigned_to_id: commissionData.assigned_to_id,
                assigned_to_name: commissionData.assigned_to_name,
                insurer: commissionData.insurer,
                broker_code: commissionData.broker_code,
                bank: commissionData.bank,
                date: commissionData.date,
                //  reconcile_for: commissionData.reconcile_for,
                bank_reconciled: commissionData.bank_reconciled,
                bank_reconciled_date: commissionData.bank_reconciled_date,
              },
            }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Data Fetched successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const updateReconciliationDataAsync =
  (
    payload: IAccountReconciliationDetailsList,
    onCallback: (
      isSuccess: boolean,
      data?: IAccountReconciliationDetailsList
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { create_ts, insert_ts, ...restPayload } = payload;

      dispatch(saveLoaderProgress());
      const res = await api.post(
        "/accounting/edit-account-reconciliation-summary",
        restPayload
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Data Saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const updateMannualReconciliationDataAsync =
  (
    payload: IAgencyBillReconciliation,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.put(
        "/accounting/update-account-transaction-items-reconciliation-record",
        {
          accn_invoice_transaction_items_id:
            payload.accn_invoice_transaction_items_id,
          commission_paid: payload.commission_paid,
          payment_paid: payload.payment_paid,
          payment_paid_date: payload.payment_paid_date,
          producer_paid: payload.producer_paid,
          producer_paid_date: payload.producer_paid_date,
          company_paid: payload.company_paid,
          company_paid_date: payload.company_paid_date,
          reconciled: payload.reconciled,
          payment_date: payload.payment_date,
        }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Data Saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const getExcelColumnMappingWithAIAsync =
  (
    data: any,
    onCallback: (isSuccess: boolean, data?: any) => void,
    reconcile_for?: string,
    selected_table?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(openLoaderWithMessage("Mappings are being Generated..."));

      const res = await makeApiCall(
        {
          url: "/general/excel-mapping",
          method: "POST",
        },
        undefined,
        {
          excel_headers: [data],
          reconcile_for: reconcile_for,
          selected_table: selected_table,
        }
      );

      const finalDocument: any = res.data.data;

      dispatch(
        showMessage({
          type: "success",
          message: "Mappings Generated successfully",
          displayAs: "snackbar",
        })
      );
      onCallback(true, finalDocument);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };
export const getExcelColumnMappingForBillPaymentsAsync =
  (
    data: any,
    onCallback: (isSuccess: boolean, data?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(openLoaderWithMessage("Mappings are being Generated..."));

      const res = await makeApiCall(
        {
          url: "/import-utility/excel-mapping-for-credit-card",
          method: "POST",
        },
        undefined,
        {
          excel_headers: [data],
        }
      );

      const finalDocument: any = res.data.data;

      dispatch(
        showMessage({
          type: "success",
          message: "Mappings Generated successfully",
          displayAs: "snackbar",
        })
      );
      onCallback(true, finalDocument);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };
export const getExcelColumnMappingForInvoicePaymentsAsync =
  (
    data: any,
    type: string,
    onCallback: (isSuccess: boolean, data?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try { 
      dispatch(openLoaderWithMessage("Mappings are being Generated..."));

      const res = await makeApiCall(
        {
          // url: "/import-utility/excel-mapping-for-bank-statement-of-invoice-and-bill",
          url: "/import-utility/excel-mapping-for-bank-statement-of-invoice-and-bill",
          method: "POST",
        },
        undefined,
        {
          excel_headers: [data],
          type: type === "bill" ? "payment": "invoice",
        }
      );

      const finalDocument: any = res.data.data;

      dispatch(
        showMessage({
          type: "success",
          message: "Mappings Generated successfully",
          displayAs: "snackbar",
        })
      );
      onCallback(true, finalDocument);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };

export const FETCH_RECONCILIATION_DETAILS_PROGRESS =
  "FETCH_RECONCILIATION_DETAILS_PROGRESS";
export const FETCH_RECONCILIATION_DETAILS_SUCCESS =
  "FETCH_RECONCILIATION_DETAILS_SUCCESS";
export const FETCH_RECONCILIATION_DETAILS_FAILED =
  "FETCH_RECONCILIATION_DETAILS_FAILED";

export const fetchReconciliationProgress = () =>
  action(FETCH_RECONCILIATION_DETAILS_PROGRESS);
export const fetchReconciliationSuccess = (
  data: IAccountReconciliationDetails
) =>
  action(FETCH_RECONCILIATION_DETAILS_SUCCESS, {
    data,
  });
export const fetchReconciliationFailed = (error: string) =>
  action(FETCH_RECONCILIATION_DETAILS_FAILED, { error });

export const fetchReconciliationDetailsAsync =
  (
    templateCode: string,
    documentCode: string,
    onCallback: (isSuccess: boolean) => void,
    updateSummary?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      await api.post("/accounting/edit-account-reconciliation-details", {
        template_code: templateCode,
        reconciliation_document_code: documentCode,
        update_summary: updateSummary,
      });
      onCallback(true);
      // const finalDocument: IAccountReconciliationDetails = res.data.data;
      // dispatch(fetchReconciliationSuccess(finalDocument));
    } catch (err: any) {
      // dispatch(fetchReconciliationFailed(ERROR_MESSAGES.NO_RECORD_FOUND));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchReconciliationDetailsSummaryAsync =
  (
    templateCode: string,
    documentCode: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchReconciliationProgress());

      const res = await api.get(
        `/accounting/get-account-reconciliation-summary?templateCode=${templateCode}&reconciliation_document_code=${documentCode}`
      );

      const resDetails = await api.get(
        `accounting/get-account-reconciliation-comparison-details?reconciliation_template_code=${templateCode}&reconciliation_document_code=${documentCode}`
      );
      const data = resDetails.data.data;

      const finalDocument: IAccountReconciliationDetails[] = res.data.data;
      if (finalDocument.length > 0) {
        dispatch(
          fetchReconciliationSuccess({
            ...finalDocument[0],
            comparison_details: (data || []).map((item: any) => {
              return {
                accn_reconciliation_comparison_details_id:
                  item.accn_reconciliation_comparison_details_id,
                comparison_details_code: item.comparison_details_code,
                reconciliation_document_code: item.reconciliation_document_code,
                reconciliation_template_code: item.reconciliation_template_code,
                mapping_code: item.mapping_code,
                primary_key: item.primary_key,

                reconciliation_status: item.reconciliation_status,
                comment: item.comment,
                status: item.status,
                created_by_id: item.created_by_id,
                created_by_name: item.created_by_name,
                modified_by_id: item.modified_by_id,
                modified_by_name: item.modified_by_name,
                create_ts: item.create_ts,
                insert_ts: item.insert_ts,
                diffs: transform(item),
              };
            }),
          })
        );
      } else {
        dispatch(fetchReconciliationFailed(ERROR_MESSAGES.NO_RECORD_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchReconciliationFailed(ERROR_MESSAGES.NO_RECORD_FOUND));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_RECONCILIATION_LIST_PROGRESS =
  " FETCH_RECONCILIATION_LIST_PROGRESS";
export const FETCH_RECONCILIATION_LIST_SUCCESS =
  " FETCH_RECONCILIATION_LIST_SUCCESS";
export const FETCH_RECONCILIATION_LIST_FAILED =
  " FETCH_RECONCILIATION_LIST_FAILED";

export const fetchReconciliationListProgress = () =>
  action(FETCH_RECONCILIATION_LIST_PROGRESS);
export const fetchReconciliationListSuccess = (
  data: IAccountReconciliationDetailsList[],
  totalRecords: number
) => action(FETCH_RECONCILIATION_LIST_SUCCESS, { data, totalRecords });
export const fetchReconciliationListFailed = () =>
  action(FETCH_RECONCILIATION_LIST_FAILED);

export const fetchReconciliationListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchReconciliationListProgress());

      let finalUrl = `/accounting/get-account-reconciliation-summary
?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/accounting/get-account-reconciliation-summary
?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IAccountReconciliationDetailsList[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchReconciliationListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchReconciliationListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const updateAccountTransactionReconciliationAsync =
  (
    comparisonData: IComparisonDetails[],
    isNewInvoice: number,
    data: IAccountReconciliationDetails,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.put(
        "/accounting/update-account-transaction-items-for-reconciliation ",
        {
          accn_invoice_transaction_items_id: comparisonData.map(
            (x) => x.primary_key
          ),
          payment_paid_date: data.payment_paid_date,
          commission_paid: data.commission_paid,
          template_code: data.template_code,
          reconciliation_document_code: data.reconciliation_document_code,
          is_new_invoice: isNewInvoice,
          reconcile_for: data.reconcile_for,
          bank_reconciled: data.bank_reconciled,
          bank_reconciled_date: data.bank_reconciled_date,
          // mapping_code: data.mapping_code,
        }
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const updateAccountManualReconciliationAsync =
  (
    comparisonData: IComparisonDetails[],
    data: IAccountReconciliationDetails,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.put(
        "/accounting/update-account-transaction-items-for-reconciliation ",
        {
          template_code: data.template_code,
          reconciliation_document_code: data.reconciliation_document_code,
          mapping_code: comparisonData.map((x) => x.mapping_code),
          manual_reconcile: 1,
        }
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const updateAccountTransactionReconciliationInvoiceAsync =
  (
    comparisonData: IComparisonDetails,
    invoiceTransactionId: number,
    isNewInvoice: number,
    data: IAccountReconciliationDetails,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.put(
        "/accounting/update-account-transaction-items-for-reconciliation ",
        {
          accn_invoice_transaction_items_id: [invoiceTransactionId],
          payment_paid_date: data.payment_paid_date,
          commission_paid: data.commission_paid,
          template_code: data.template_code,
          reconciliation_document_code: data.reconciliation_document_code,
          is_new_invoice: isNewInvoice,
          mapping_code: comparisonData.mapping_code,
        }
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchPoliciesByCustomerPolicyNumber =
  (
    policyNumber: string,
    onCallback: (isSuccess: boolean, list?: { [key: string]: any }[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-all-customer-policy-details?policy_number=${policyNumber}`
      );
      const policiesList: { [key: string]: any }[] = res.data.data || [];

      onCallback(true, policiesList);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_RECONCILIATION_DETAILS_STATE =
  "CLEAR_RECONCILIATION_DETAILS_STATE";
export const clearReconciliationDetailsState = () =>
  action(CLEAR_RECONCILIATION_DETAILS_STATE);
